import { Badge } from "@/components/ui/badge";
import { Circle, AlertCircle, Pause, Loader2, Clock } from "lucide-react";
import type { ResourceStatus } from "@/types/admin";
import { cn } from "@/lib/utils";

interface ResourceStatusBadgeProps {
  status: ResourceStatus;
  withIcon?: boolean;
  className?: string;
}

const getStatusConfig = (status: ResourceStatus) => {
  const configs: Record<
    ResourceStatus,
    {
      label: string;
      variant: "default" | "secondary" | "destructive" | "outline";
      icon: React.ReactNode;
      color: string;
      /** Merged after variant; used for semantic chips (matches VM list table). */
      badgeClassName?: string;
      /** Solid background color for the compact "dot" display variant. */
      dotClassName: string;
      /** Ring/border color for status indicators drawn around an avatar or icon. */
      ringClassName: string;
    }
  > = {
    Pending: {
      label: "Pending",
      variant: "outline",
      icon: <Clock className="h-3 w-3 text-warn" />,
      color: "text-warn",
      badgeClassName:
        "border-warn/45 bg-warn-soft text-warn shadow-none dark:border-warn/30",
      dotClassName: "bg-warn",
      ringClassName: "ring-warn",
    },
    Provisioning: {
      label: "Provisioning",
      variant: "secondary",
      icon: <Loader2 className="h-3 w-3 animate-spin" />,
      color: "text-warn",
      badgeClassName:
        "border-warn/45 bg-warn-soft text-warn shadow-none dark:border-warn/25",
      dotClassName: "bg-warn animate-pulse",
      ringClassName: "ring-warn",
    },
    Running: {
      label: "Running",
      variant: "outline",
      icon: <Circle className="h-3 w-3 shrink-0 fill-ok text-ok" />,
      color: "text-ok",
      badgeClassName:
        "border-ok/55 bg-ok-soft text-ok shadow-none hover:bg-ok-soft/90 focus-visible:ring-ok/35 dark:border-ok/30 dark:focus-visible:ring-ok/40",
      dotClassName: "bg-ok",
      ringClassName: "ring-ok",
    },
    Stopped: {
      label: "Stopped",
      variant: "outline",
      icon: <Circle className="h-3 w-3 shrink-0 text-muted-foreground" />,
      color: "text-muted-foreground",
      badgeClassName:
        "border-border bg-muted/50 text-muted-foreground shadow-none dark:border-border/80 dark:bg-muted/40",
      dotClassName: "bg-muted-foreground",
      ringClassName: "ring-muted-foreground",
    },
    Suspended: {
      label: "Suspended",
      variant: "outline",
      icon: <Pause className="h-3 w-3 shrink-0 text-warn" />,
      color: "text-warn",
      badgeClassName:
        "border-warn/45 bg-warn-soft text-warn shadow-none dark:border-warn/30",
      dotClassName: "bg-warn",
      ringClassName: "ring-warn",
    },
    Error: {
      label: "Error",
      variant: "outline",
      icon: <AlertCircle className="h-3 w-3 shrink-0 text-destructive" />,
      color: "text-destructive",
      badgeClassName:
        "border-destructive/45 bg-destructive-soft text-destructive shadow-none dark:border-destructive/30",
      dotClassName: "bg-destructive",
      ringClassName: "ring-destructive",
    },
    Terminating: {
      label: "Terminating",
      variant: "outline",
      icon: (
        <Loader2 className="h-3 w-3 shrink-0 animate-spin text-destructive" />
      ),
      color: "text-destructive",
      badgeClassName:
        "border-destructive/45 bg-destructive-soft text-destructive shadow-none dark:border-destructive/30",
      dotClassName: "bg-destructive animate-pulse",
      ringClassName: "ring-destructive",
    },
    // Where this badge renders (detail page, machine picker) a deleted row never appears, so a
    // Decommissioning row here is always a reinstall in flight — expected to come back, so it reads
    // like Provisioning rather than like Terminating. The label is the stored status (2026-10-14;
    // it read "Reinstalling" before).
    Decommissioning: {
      label: "Decommissioning",
      variant: "secondary",
      icon: <Loader2 className="h-3 w-3 shrink-0 animate-spin text-warn" />,
      color: "text-warn",
      badgeClassName:
        "border-warn/45 bg-warn-soft text-warn shadow-none dark:border-warn/25",
      dotClassName: "bg-warn animate-pulse",
      ringClassName: "ring-warn",
    },
    Terminated: {
      label: "Terminated",
      variant: "outline",
      icon: <Circle className="h-3 w-3 shrink-0 text-muted-foreground" />,
      color: "text-muted-foreground",
      badgeClassName:
        "border-border bg-muted/45 text-muted-foreground shadow-none dark:border-border/80 dark:bg-muted/30",
      dotClassName: "bg-muted-foreground",
      ringClassName: "ring-muted-foreground",
    },
  };

  return configs[status];
};

/**
 * Ring (avatar border) and dot-badge classes for a status, used when the status is shown
 * as a small colored indicator on a resource's icon/avatar rather than as a badge or label.
 * Falls back to a neutral border/dot for statuses without a known config.
 */
export function getResourceStatusIndicator(status: ResourceStatus): {
  ringClassName: string;
  dotClassName: string;
} {
  const config = getStatusConfig(status);
  return config
    ? { ringClassName: config.ringClassName, dotClassName: config.dotClassName }
    : { ringClassName: "ring-border", dotClassName: "bg-muted-foreground" };
}

export function ResourceStatusBadge({
  status,
  withIcon = true,
  className,
}: ResourceStatusBadgeProps) {
  const config = getStatusConfig(status);

  if (!config) {
    return (
      <Badge variant="secondary" className={className}>
        {status}
      </Badge>
    );
  }

  return (
    <Badge
      variant={config.variant}
      className={cn(
        "inline-flex min-h-8 items-center gap-1.5 px-2.5 font-medium leading-none",
        config.badgeClassName,
        className,
      )}
    >
      {withIcon && (
        <span className={cn("flex shrink-0 items-center", config.color)}>
          {config.icon}
        </span>
      )}
      <span className="truncate">{config.label}</span>
    </Badge>
  );
}
